/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Button } from 'reactstrap';
import LoadingPlaceholder from '../../LoaderContainer/LoadingPlaceholder';
import { languages } from '../../custom-metrics/constants';
import { getTooltip } from './questionUtil';
import { Instructions } from './Instructions';
import UniversalTracker from '../../../model/UniversalTracker';
import { useAppSelector } from '../../../reducers';
import { loadGlossary } from '../../../slice/glossarySlice';
import { useDispatch } from 'react-redux';
import './QuestionInformation.scss';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ReadonlyRichTextEditor } from '@components/rich-text-editor/ReadonlyRichTextEditor';
import { RichTextEditorContainer } from '@components/rich-text-editor/RichTextEditor';

export interface QuestionInformationProps {
  className?: string;
  utr?: UniversalTracker,
  alternativeCode: string;
}

const QuestionInformationInner = (props: QuestionInformationProps) => {
  const { className, utr, alternativeCode } = props;

  const dispatch = useDispatch();
  const _timeout = React.useRef<ReturnType<typeof setTimeout> | undefined>();
  const [startAltTextAnimation, setStartAltTextAnimation] = useState(false);
  const [selectedLanguage, setLanguage] = useState<string | undefined>(undefined);
  const [editor] = useLexicalComposerContext();

  const setSelectedLanguage = (language?: string) => {
    if (!language) {
      setStartAltTextAnimation(false);
      if (_timeout.current) {
        clearTimeout(_timeout.current);
      }
      _timeout.current = setTimeout(() => setLanguage(language), 500);
    } else {
      setLanguage(language);
      setStartAltTextAnimation(true);
    }
  }

  React.useEffect(() => {
    dispatch(loadGlossary())
  }, [dispatch])

  const glossaryState = useAppSelector((state) => state.glossary.data);

  const valueLabel: string = utr ? utr.getValueLabel(alternativeCode) : '';
  const altValueLabel: string = utr ? utr.getValueLabel(selectedLanguage ?? alternativeCode) : '';
  const languageOptions = utr?.getAlternativeLanguages();

  const instructionsEditorState = utr?.getInstructionsEditorState(alternativeCode);

  return (
    <div className={`questionInformation ${className ?? ''}`}>
      <div className='questionTitle strong' >
        {languageOptions && languageOptions.length > 0 ?
          <div className='animated-container'>
            <div className={`buttons-animated ${!startAltTextAnimation ? '' : 'disappear-right'}`}>
              {languages
                .filter(l => languageOptions.includes(l.code))
                .map(l => (
                  <Button key={`lang_${l.code}`} color='link' size='sm' className='pl-0 mr-2 language-link' onClick={() => setSelectedLanguage(l.code)}>
                    {l.nativeName}<i className='ml-2 far fa-arrow-alt-circle-right' />
                  </Button>
                ))
              }
            </div>

            <div className={`buttons-animated ${startAltTextAnimation ? '' : 'disappear-left'}`}>
              <Button color='link' size='sm' className='pl-0 mr-2' onClick={() => setSelectedLanguage()}>
                <i className='far fa-arrow-alt-circle-left mr-2' />English
              </Button>
            </div>
          </div>
          : null
        }
        <div className='animated-container'>
          <div className={`text-xl questionTitle__heading text-animated ${!startAltTextAnimation ? '' : 'disappear-right'}`}>
            {valueLabel ? getTooltip(valueLabel, glossaryState) : <LoadingPlaceholder height={53} />}
          </div>
          <div className={`text-xl questionTitle__heading text-animated ${startAltTextAnimation ? '' : 'disappear-left'}`}>
            {altValueLabel ? getTooltip(altValueLabel, glossaryState) : <LoadingPlaceholder height={53} />}
          </div>
        </div>
      </div>
      <div className='mt-3'>
        {instructionsEditorState ? (
          <ReadonlyRichTextEditor editorState={editor.parseEditorState(instructionsEditorState)} />
        ) : (
          <Instructions {...{ utr, glossaryState, alternativeCode, selectedLanguage, startAltTextAnimation }} />
        )}
      </div>
    </div>
  );
}

export const QuestionInformation = (props: QuestionInformationProps) => {
  return (
    <RichTextEditorContainer>
      <QuestionInformationInner {...props} />
    </RichTextEditorContainer>
  );
};
